const MOBILE_QUERY = '(max-width: 749px)';
const DRAG_THRESHOLD = 6;

class ExpressLifestyleCarousel {
  constructor(scroller) {
    this.scroller = scroller;
    this.mediaQuery = window.matchMedia(MOBILE_QUERY);
    this.abortController = undefined;
    this.dragMoved = false;
    this.startX = 0;
    this.startScrollLeft = 0;

    this.mediaQuery.addEventListener('change', this.handleBreakpointChange);
    this.handleBreakpointChange();
  }

  handleBreakpointChange = () => {
    if (this.mediaQuery.matches) {
      this.enable();
      return;
    }

    this.disable();
  };

  enable() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.scroller.dataset.dragScroll = 'true';
    this.scroller.addEventListener('pointerdown', this.handlePointerDown, {
      signal: this.abortController.signal,
    });
    this.scroller.addEventListener('wheel', this.handleWheel, {
      passive: false,
      signal: this.abortController.signal,
    });
  }

  disable() {
    this.abortController?.abort();
    this.abortController = undefined;
    this.scroller.removeAttribute('data-drag-scroll');
    this.scroller.removeAttribute('data-dragging');
  }

  handlePointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (this.scroller.scrollWidth <= this.scroller.clientWidth) return;

    this.dragMoved = false;
    this.startX = event.clientX;
    this.startScrollLeft = this.scroller.scrollLeft;

    const controller = new AbortController();
    const { signal } = controller;

    document.addEventListener('pointermove', this.handlePointerMove, { signal });
    document.addEventListener('pointerup', () => this.handlePointerEnd(event, controller), { signal });
    document.addEventListener('pointercancel', () => this.handlePointerEnd(event, controller), { signal });
  };

  handleWheel = (event) => {
    if (!this.mediaQuery.matches || this.scroller.scrollWidth <= this.scroller.clientWidth) return;
    if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;

    const maxScrollLeft = this.scroller.scrollWidth - this.scroller.clientWidth;
    const direction = Math.sign(event.deltaY);
    const canScroll = direction > 0 ? this.scroller.scrollLeft < maxScrollLeft : this.scroller.scrollLeft > 0;

    if (!canScroll) return;

    event.preventDefault();
    this.scroller.scrollLeft = Math.min(Math.max(this.scroller.scrollLeft + event.deltaY, 0), maxScrollLeft);
  };

  handlePointerMove = (event) => {
    const deltaX = this.startX - event.clientX;

    if (!this.dragMoved && Math.abs(deltaX) < DRAG_THRESHOLD) return;

    if (!this.dragMoved) {
      this.dragMoved = true;
      this.scroller.dataset.dragging = 'true';
      this.scroller.setPointerCapture(event.pointerId);
    }

    event.preventDefault();
    this.scroller.scrollLeft = this.startScrollLeft + deltaX;
  };

  handlePointerEnd(event, controller) {
    controller.abort();

    if (this.dragMoved) {
      document.addEventListener('click', this.preventNextClick, { once: true, capture: true });
    }

    if (this.scroller.hasPointerCapture(event.pointerId)) {
      this.scroller.releasePointerCapture(event.pointerId);
    }

    this.scroller.removeAttribute('data-dragging');
  }

  preventNextClick = (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  destroy() {
    this.disable();
    this.mediaQuery.removeEventListener('change', this.handleBreakpointChange);
  }
}

const carousels = new WeakMap();

function initExpressLifestyleCarousels(root = document) {
  root.querySelectorAll('.express-lifestyle__brands').forEach((scroller) => {
    if (!(scroller instanceof HTMLElement) || carousels.has(scroller)) return;

    carousels.set(scroller, new ExpressLifestyleCarousel(scroller));
  });
}

initExpressLifestyleCarousels();

document.addEventListener('shopify:section:load', (event) => {
  initExpressLifestyleCarousels(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('.express-lifestyle__brands').forEach((scroller) => {
    const carousel = carousels.get(scroller);

    if (!carousel) return;

    carousel.destroy();
    carousels.delete(scroller);
  });
});
